export type MarketingSite = "playr" | "migrate";

export const MIGRATION_HOST_STEPS = {
  signin: "signin",
  verify: "verify",
  sources: "sources",
  // /wallet already belongs to the platform's wallet routes.
  wallet: "wallet-address",
  vault: "vault",
  done: "done",
} as const;
const STEPS = new Set<string>(Object.values(MIGRATION_HOST_STEPS));

function browserUrl(): URL | undefined {
  return typeof window === "undefined"
    ? undefined
    : new URL(window.location.href);
}

/** Only the dedicated hosts opt into root/step aliases; normal app URLs stay valid. */
export function marketingSite(
  url: URL | undefined = browserUrl()
): MarketingSite | null {
  const match = url?.hostname.match(
    /^(playr|migrate)\.(?:staging\.)?funtico\.com$/
  );
  return (match?.[1] as MarketingSite | undefined) ?? null;
}

function staging(url?: URL): boolean {
  if (
    url?.hostname.endsWith(".staging.funtico.com") ||
    url?.hostname === "staging.funtico.com"
  )
    return true;
  if (
    url &&
    [
      "funtico.com",
      "www.funtico.com",
      "playr.funtico.com",
      "migrate.funtico.com",
    ].includes(url.hostname)
  )
    return false;
  if (import.meta.env.VITE_ENVIRONMENT === "staging") return true;
  if (import.meta.env.VITE_ENVIRONMENT === "production") return false;
  try {
    return new URL(import.meta.env.VITE_REACT_APP_BASE_URL).hostname
      .split(".")
      .includes("staging");
  } catch {
    return false;
  }
}

function origin(site: MarketingSite | "platform", url?: URL): string {
  const domain = staging(url) ? "staging.funtico.com" : "funtico.com";
  return `https://${site === "platform" ? "" : `${site}.`}${domain}`;
}

function pathSite(pathname: string): MarketingSite | null {
  if (/^\/migrate(?:\/|$)/.test(pathname)) return "migrate";
  if (/^\/playr(?:\/|$)/.test(pathname)) return "playr";
  return null;
}

/** Full URLs for links between sites; local development keeps its existing routes. */
export function siteHref(
  path: string,
  url: URL | undefined = browserUrl()
): string {
  if (!path.startsWith("/") || path.startsWith("//")) return path;
  if (url && !/(?:^|\.)funtico\.com$/.test(url.hostname)) return path;
  const destination = new URL(path, "https://funtico.com");
  const site = pathSite(destination.pathname);
  if (site)
    destination.pathname =
      destination.pathname.replace(new RegExp(`^/${site}(?=/|$)`), "") || "/";
  if (
    site === "migrate" &&
    destination.pathname.replace(/\/$/, "") === "/wallet"
  ) {
    destination.pathname = "/wallet-address";
  }
  return `${origin(site ?? "platform", url)}${destination.pathname}${destination.search}${destination.hash}`;
}

export function migrationStepPath(
  path: string,
  url: URL | undefined = browserUrl()
): string {
  if (marketingSite(url) !== "migrate") return path;
  const step = path.replace(
    /^\/migrate(?:\/|$)/,
    ""
  ) as keyof typeof MIGRATION_HOST_STEPS;
  return Object.hasOwn(MIGRATION_HOST_STEPS, step)
    ? `/${MIGRATION_HOST_STEPS[step]}`
    : path === "/migrate"
      ? "/"
      : path;
}

export function migrationRoutePath(
  pathname: string,
  url: URL | undefined = browserUrl()
): string {
  if (marketingSite(url) !== "migrate") return pathname;
  const step = Object.entries(MIGRATION_HOST_STEPS).find(
    ([, path]) => pathname.replace(/\/$/, "") === `/${path}`
  )?.[0];
  return step ? `/migrate/${step}` : pathname;
}

/** Repair old prefixed links and send unrelated pages back to their own site. */
export function marketingRedirect(url: URL): string | null {
  const site = marketingSite(url);
  if (!site) return null;
  const destination = new URL(url);
  const prefixedSite = pathSite(url.pathname);
  if (prefixedSite) {
    destination.hostname = new URL(origin(prefixedSite, url)).hostname;
    destination.pathname =
      url.pathname.replace(new RegExp(`^/${prefixedSite}(?=/|$)`), "") || "/";
    if (
      prefixedSite === "migrate" &&
      destination.pathname.replace(/\/$/, "") === "/wallet"
    )
      destination.pathname = "/wallet-address";
  } else if (
    site === "migrate" &&
    STEPS.has(url.pathname.replace(/^\/|\/$/g, ""))
  ) {
    destination.pathname = url.pathname.replace(/\/$/, "");
  } else if (url.pathname !== "/") {
    destination.hostname = new URL(origin("platform", url)).hostname;
  }
  return destination.href === url.href ? null : destination.href;
}
